import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

export default function SignupSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setError("Missing session id");
      return;
    }
    let cancelled = false;
    let polls = 0;

    const poll = async () => {
      if (cancelled) return;
      polls += 1;
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${apiUrl}/api/checkout/status/${sessionId}`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to fetch status");
        const data = await res.json();
        if (data.payment_status === "paid") {
          // Pull the updated user (plan now active) into context
          await refresh();
          setStatus("paid");
          setTimeout(() => navigate("/onboarding"), 1500);
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (polls >= MAX_POLLS) {
          setStatus("failed");
          setError("Timed out waiting for payment confirmation");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (polls >= MAX_POLLS) {
          setStatus("failed");
          setError(e.message || "Could not verify payment");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-5" data-testid="signup-success-page">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#1D6AFF]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)] text-center">
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>
        {status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center mb-5">
              <Loader2 className="w-7 h-7 text-[#1D6AFF] animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Confirming your payment…</h1>
            <p className="text-sm text-slate-500">Stripe is finalizing the transaction. This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 border border-emerald-300 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Payment confirmed</h1>
            <p className="text-sm text-slate-500 mb-5">Setting up your workspace…</p>
            <button
              data-testid="success-go-dashboard"
              onClick={() => navigate("/onboarding")}
              className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5"
            >
              Start setup <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mb-5">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              {status === "expired" ? "Payment session expired" : "Payment not confirmed"}
            </h1>
            <p className="text-sm text-slate-500 mb-5">{error || "Please try again or contact support if your card was charged."}</p>
            <button
              data-testid="success-retry"
              onClick={() => navigate("/signup")}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl px-5 py-2.5"
            >
              Back to signup
            </button>
          </>
        )}
      </div>
    </div>
  );
}
